import { apiFetch, apiJson } from "@/lib/api-client";

const PERMISSION_KEY = "desktop-notifications-permission";

function urlBase64ToUint8Array(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }

  return output;
}

function supportsWebPush() {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function getNotificationPermission(): NotificationPermission | null {
  if (!("Notification" in window)) return null;
  return Notification.permission;
}

async function subscribeToWebPush() {
  if (!supportsWebPush()) return;

  const { publicKey } = await apiJson<{ publicKey: string }>(
    "/api/push-subscriptions/public-key",
  );
  const registration =
    (await navigator.serviceWorker.getRegistration()) ??
    (import.meta.env.PROD
      ? await navigator.serviceWorker.register("/sw.js")
      : undefined);
  if (!registration) return;

  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription =
    existingSubscription ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await apiFetch("/api/push-subscriptions", {
    method: "POST",
    body: JSON.stringify(subscription.toJSON()),
  });
}

export async function requestNotificationPermission(): Promise<NotificationPermission | null> {
  if (!supportsWebPush()) return null;

  const permission = await Notification.requestPermission();
  localStorage.setItem(PERMISSION_KEY, permission);
  if (permission === "granted") {
    await subscribeToWebPush().catch(() => {
      // Permission and subscription are separate browser/server capabilities.
    });
  }
  return permission;
}

export async function unsubscribeFromWebPush() {
  if (!supportsWebPush()) return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await subscription.unsubscribe();
  await apiFetch("/api/push-subscriptions", {
    method: "DELETE",
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
}
